// Apolo IDE — HandsFreeOrb (Sprint v11 E15 voice modal).
//
// Pure presentational sphere that reacts to the hands-free conversation state.
// No text — only color + motion. Animations are deterministic per state
// (no audio analyser) for consistency across browser/Electron paths.
//
// States: off (gray idle) → connecting (cyan spin-up while the API session opens)
// → listening (blue pulse) → transcribing (indigo fast pulse) → thinking (violet
// rotate) → speaking (green expand).

import { memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const STATE_CONFIG = {
  off: {
    color: '#525252',
    colorLight: '#737373',
    glow: 'rgba(82, 82, 82, 0.25)',
    scale: [1, 1, 1],
    rotate: 0,
    duration: 2,
  },
  // Loading: the click fired and the voice session is negotiating (SDP/ICE, mic).
  // A steady cyan spin reads as "spinning up" so the user waits instead of talking
  // into a socket that isn't open yet.
  connecting: {
    color: '#0891b2',
    colorLight: '#67e8f9',
    glow: 'rgba(8, 145, 178, 0.55)',
    scale: [1, 1.05, 1],
    rotate: 360,
    duration: 1.1,
  },
  listening: {
    color: '#3b82f6',
    colorLight: '#93c5fd',
    glow: 'rgba(59, 130, 246, 0.55)',
    scale: [1, 1.14, 1],
    rotate: 0,
    duration: 1.6,
  },
  transcribing: {
    color: '#6366f1',
    colorLight: '#a5b4fc',
    glow: 'rgba(99, 102, 241, 0.55)',
    scale: [1, 1.09, 1],
    rotate: 0,
    duration: 0.8,
  },
  thinking: {
    color: '#8b5cf6',
    colorLight: '#c4b5fd',
    glow: 'rgba(139, 92, 246, 0.55)',
    scale: [1, 1.07, 1],
    rotate: 360,
    duration: 2.5,
  },
  speaking: {
    color: '#6f9e78',
    colorLight: '#a7c7ab',
    glow: 'rgba(111, 158, 120, 0.55)',
    scale: [1, 1.2, 0.95, 1.15, 1],
    rotate: 0,
    duration: 1.3,
  },
};

const ORB_SIZE = 200;
const RIPPLE_COUNT = 3;

/**
 * @param {{ state: 'off'|'connecting'|'listening'|'transcribing'|'thinking'|'speaking' }} props
 */
function HandsFreeOrb({ state = 'off' }) {
  const cfg = STATE_CONFIG[state] || STATE_CONFIG.off;
  const reduceMotion = useReducedMotion();
  const animate = reduceMotion ? { scale: 1, rotate: 0 } : { scale: cfg.scale, rotate: cfg.rotate };
  /** @type {import('framer-motion').Transition} */
  const transition = reduceMotion
    ? { duration: 0 }
    : { duration: cfg.duration, repeat: state === 'off' ? 0 : Infinity, ease: 'easeInOut' };

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: ORB_SIZE * 2, height: ORB_SIZE * 2 }}
    >
      {/* Ripple rings — expand outward while listening/speaking */}
      {!reduceMotion && state !== 'off' &&
        Array.from({ length: RIPPLE_COUNT }).map((_, i) => (
          <motion.span
            key={i}
            aria-hidden="true"
            className="absolute rounded-full"
            style={{
              width: ORB_SIZE,
              height: ORB_SIZE,
              border: `2px solid ${cfg.color}`,
            }}
            animate={{
              scale: [1, 1.8],
              opacity: [0.4, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: (i * 2) / RIPPLE_COUNT,
              ease: 'easeOut',
            }}
          />
        ))}

      {/* Outer glow */}
      <motion.div
        aria-hidden="true"
        className="absolute rounded-full"
        style={{
          width: ORB_SIZE * 1.5,
          height: ORB_SIZE * 1.5,
          background: `radial-gradient(circle, ${cfg.glow} 0%, transparent 70%)`,
        }}
        animate={reduceMotion ? { opacity: 0.5 } : { opacity: [0.3, 0.7, 0.3] }}
        transition={/** @type {import('framer-motion').Transition} */ ({ duration: cfg.duration, repeat: reduceMotion ? 0 : Infinity, ease: 'easeInOut' })}
      />

      {/* Main sphere */}
      <motion.div
        role="img"
        aria-label={`Esfera de voz — estado: ${state}`}
        className="relative rounded-full"
        style={{
          width: ORB_SIZE,
          height: ORB_SIZE,
          background: `radial-gradient(circle at 35% 30%, ${cfg.colorLight} 0%, ${cfg.color} 50%, ${cfg.color} 100%)`,
          boxShadow: `0 0 60px ${cfg.glow}, inset 0 -20px 40px rgba(0,0,0,0.3), inset 0 20px 40px rgba(255,255,255,0.1)`,
        }}
        animate={animate}
        transition={transition}
      >
        {/* Glossy highlight — fake 3D sphere depth */}
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{
            top: '15%',
            left: '25%',
            width: '30%',
            height: '25%',
            background: 'radial-gradient(ellipse, rgba(255,255,255,0.35) 0%, transparent 70%)',
            filter: 'blur(4px)',
          }}
        />
      </motion.div>
    </div>
  );
}

export default memo(HandsFreeOrb);
